/**
 * Ajustes (SDD §7.2): perfil, extensión conectada, consentimiento, exportar
 * datos, cerrar sesión en todas partes y eliminar la cuenta.
 */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import {
  STANDARD_TEMPLATE_IDS,
  TEMPLATES,
  type PatchMeInput,
  type StandardTemplateId
} from '@eco/shared'
import { useApp, useToast, errorText } from '../components/AppContext'
import { CustomTemplatesSection } from '../components/CustomTemplates'
import { IntegrationsSection } from '../components/Integrations'
import { TrackersSection } from '../components/Trackers'
import { VocabularySection } from '../components/Vocabulary'
import { OrgPrivacySection } from '../components/OrgPrivacy'
import { Modal } from '../components/ui'
import { auth } from '../lib/auth'
import { connectExtension, detectExtension, type ExtensionInfo } from '../lib/extension'
import { formatDate } from '../lib/format'
import { useDocumentTitle } from '../lib/hooks'

export function Settings() {
  useDocumentTitle('Ajustes')
  const { api, me, refreshMe } = useApp()
  const toast = useToast()
  const nav = useNavigate()
  const [nombre, setNombre] = useState('')
  const [ext, setExt] = useState<ExtensionInfo | null | undefined>(undefined)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [confirmText, setConfirmText] = useState('')
  const [deleting, setDeleting] = useState(false)
  const [exporting, setExporting] = useState(false)
  useEffect(() => setNombre(me?.user.nombre ?? ''), [me])
  useEffect(() => {
    void detectExtension().then(setExt)
  }, [])

  async function saveName() {
    try {
      await api.patchMe({ nombre: nombre.trim() || null })
      await refreshMe()
      toast('Guardado')
    } catch (e) {
      toast(errorText(e), 'error')
    }
  }

  async function savePrefs(patch: PatchMeInput) {
    try {
      await api.patchMe(patch)
      await refreshMe()
      toast('Guardado')
    } catch (e) {
      toast(errorText(e), 'error')
    }
  }

  async function connect() {
    if (!ext) return
    try {
      const { hashedToken } = await api.extensionLink()
      const ok = await connectExtension(ext.id, hashedToken)
      toast(
        ok ? 'Extensión conectada' : 'La extensión no respondió. Ábrela y vuelve a intentar.',
        ok ? 'ok' : 'error'
      )
      setExt(await detectExtension())
    } catch (e) {
      toast(errorText(e), 'error')
    }
  }

  async function exportData() {
    if (exporting) return
    setExporting(true)
    try {
      await api.exportData()
      toast('Te enviaremos un correo con el enlace de descarga en unos minutos.')
    } catch (e) {
      toast(errorText(e), 'error')
    } finally {
      setExporting(false)
    }
  }

  async function deleteAccount() {
    if (deleting) return
    setDeleting(true)
    try {
      await api.deleteAccount()
      await auth.signOut()
      nav('/login')
    } catch (e) {
      toast(errorText(e), 'error')
      setDeleting(false)
    }
  }

  return (
    <div className="page narrow">
      <div className="page-head">
        <h1 className="grow">Ajustes</h1>
      </div>
      <div className="stack">
        <section className="card stack">
          <h2>Tu perfil</h2>
          <dl className="kv">
            <dt>Correo</dt>
            <dd>{me?.user.email}</dd>
            <dt>Cuenta creada</dt>
            <dd>{me ? formatDate(me.user.creadoEn) : ''}</dd>
          </dl>
          <form
            className="row"
            onSubmit={(e) => {
              e.preventDefault()
              void saveName()
            }}
          >
            <label className="field grow">
              <span>Tu nombre (aparece en lo que compartes)</span>
              <input value={nombre} maxLength={120} onChange={(e) => setNombre(e.target.value)} />
            </label>
            <button className="btn" style={{ alignSelf: 'flex-end' }}>
              Guardar
            </button>
          </form>
        </section>

        <section className="card stack">
          <h2>Extensión del navegador</h2>
          {ext === undefined ? (
            <p className="faint">Buscando la extensión…</p>
          ) : ext === null ? (
            <div className="row wrap">
              <p className="muted grow" style={{ margin: 0 }}>
                No encontramos la extensión de eco en este navegador.
              </p>
              <a className="btn" href="/bienvenida">
                Instalar
              </a>
            </div>
          ) : (
            <div className="row wrap">
              <p className="grow" style={{ margin: 0 }}>
                {ext.connected ? '✓ Conectada' : 'Instalada, sin conectar'}{' '}
                <span className="faint">· versión {ext.version}</span>
              </p>
              <button className="btn" onClick={() => void connect()}>
                {ext.connected ? 'Volver a conectar' : 'Conectar'}
              </button>
            </div>
          )}
          <p className="faint" style={{ fontSize: 12 }}>
            El micrófono, la copia local del audio y el atajo de teclado se configuran desde el
            ícono de eco en la barra del navegador.
          </p>
        </section>

        <section className="card stack">
          <h2>Notas y correos</h2>
          <label className="field">
            <span>Plantilla para las notas automáticas</span>
            <select
              value={me?.user.plantillaDefecto ?? 'reunion_general'}
              onChange={(e) =>
                void savePrefs({ plantillaDefecto: e.target.value as StandardTemplateId })
              }
            >
              {STANDARD_TEMPLATE_IDS.map((t) => (
                <option key={t} value={t}>
                  {TEMPLATES[t].nombre}
                </option>
              ))}
            </select>
          </label>
          <p className="faint" style={{ fontSize: 12, marginTop: -6 }}>
            {TEMPLATES[me?.user.plantillaDefecto ?? 'reunion_general'].descripcion} Siempre puedes
            generar las otras desde cada reunión.
          </p>
          <label className="check">
            <input
              type="checkbox"
              checked={me?.user.notificaciones.notas_listas ?? true}
              onChange={(e) =>
                void savePrefs({ notificaciones: { notas_listas: e.target.checked } })
              }
            />
            <span>Avisarme por correo cuando las notas estén listas</span>
          </label>
          <label className="check">
            <input
              type="checkbox"
              checked={me?.user.notificaciones.cuota ?? true}
              onChange={(e) => void savePrefs({ notificaciones: { cuota: e.target.checked } })}
            />
            <span>Avisarme cuando me queden pocas horas del mes</span>
          </label>
          <label className="check">
            <input
              type="checkbox"
              checked={me?.user.notificaciones.resumen_semanal ?? true}
              onChange={(e) =>
                void savePrefs({ notificaciones: { resumen_semanal: e.target.checked } })
              }
            />
            <span>Los lunes, un resumen con las tareas y decisiones de la semana</span>
          </label>
          <label className="check">
            <input
              type="checkbox"
              checked={me?.user.notificaciones.consejos ?? true}
              onChange={(e) => void savePrefs({ notificaciones: { consejos: e.target.checked } })}
            />
            <span>Consejos y recordatorios de eco</span>
          </label>
        </section>

        <CustomTemplatesSection />

        <TrackersSection />

        <VocabularySection />

        <OrgPrivacySection />

        <IntegrationsSection />

        <section className="card stack">
          <h2>Aviso a los participantes</h2>
          {me?.user.consentimientoAceptadoEn ? (
            <p className="muted" style={{ margin: 0 }}>
              Aceptaste avisar a los participantes antes de grabar el{' '}
              {formatDate(me.user.consentimientoAceptadoEn)}. En Chile, grabar una conversación
              privada sin consentimiento puede ser delito.
            </p>
          ) : (
            <div className="row wrap">
              <p className="grow" style={{ margin: 0 }}>
                Confirmo que avisaré a los participantes antes de grabar.
              </p>
              <button
                className="btn"
                onClick={() =>
                  void api
                    .patchMe({ aceptarConsentimiento: true })
                    .then(() => refreshMe())
                    .catch((e) => toast(errorText(e), 'error'))
                }
              >
                Aceptar
              </button>
            </div>
          )}
        </section>

        <section className="card stack">
          <h2>Tus datos</h2>
          <div className="row wrap">
            <p className="grow muted" style={{ margin: 0 }}>
              Descarga todas tus transcripciones y notas en un archivo JSON (te llega el link por
              correo).
            </p>
            <button className="btn" disabled={exporting} onClick={() => void exportData()}>
              {exporting ? 'Pidiendo…' : 'Exportar mis datos'}
            </button>
          </div>
          <div className="row wrap">
            <p className="grow muted" style={{ margin: 0 }}>
              Cierra la sesión en este y en todos tus dispositivos, incluida la extensión.
            </p>
            <button className="btn" onClick={() => void auth.signOut().then(() => nav('/login'))}>
              Cerrar sesión en todas partes
            </button>
          </div>
          <div className="row wrap">
            <p className="grow muted" style={{ margin: 0 }}>
              Elimina tu cuenta, tus grabaciones y tus notas. No se puede deshacer.
            </p>
            <button className="btn danger" onClick={() => setConfirmDelete(true)}>
              Eliminar cuenta
            </button>
          </div>
        </section>
      </div>

      <Modal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="Eliminar tu cuenta"
        footer={
          <>
            <button className="btn ghost" onClick={() => setConfirmDelete(false)}>
              Cancelar
            </button>
            <button
              className="btn live"
              disabled={deleting || confirmText.trim().toUpperCase() !== 'ELIMINAR'}
              onClick={() => void deleteAccount()}
            >
              {deleting ? 'Eliminando…' : 'Eliminar definitivamente'}
            </button>
          </>
        }
      >
        <p>
          Se cancela tu suscripción, se desactivan los links compartidos y se borran tus
          grabaciones, transcripciones y notas. Los respaldos se purgan en un máximo de 7 días.
        </p>
        <label className="field">
          <span>Escribe ELIMINAR para confirmar</span>
          <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} />
        </label>
      </Modal>
    </div>
  )
}
